import { Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Maybe,
  NewStoreItemsQuery,
  UserCollectionBoxFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import { CollectionBox, CollectionBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  hasNextPage: boolean
  userCollections: Maybe<{ node: UserCollectionBoxFragment }[]>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

type CollectionBannerCellProps = Pick<
  State,
  'loading' | 'hasNextPage' | 'userCollections'
> &
  Action

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

type NewStoresCellProps = {
  loading: State['loading']
  storeItems?: NewStoreItemsQuery['items']
  artistId: string
  onSelectItem: () => void
}

export const ArtistMyCollectionsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistCollections')
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <>
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-4516
       */}
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
            hasSettingTask:
              !!state.user?.isMembershipPersonalInformationRequired,
          },
        }}
        margin
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount ||
                !!state.user?.isMembershipPersonalInformationRequired
              }
            />
          ),
          // right: <IconButton.Search edge="end" />,
        }}
      >
        <Head title={routerNames.ArtistMyCollections} />
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
        <CollectionBannerCell
          userCollections={state.userCollections}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
        />
      </BaseLayout>
    </>
  )
}

const CollectionBannerCell: Cell<CollectionBannerCellProps> = ({
  loading,
  hasNextPage,
  userCollections,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistCollections')
  const { analytics } = useAnalytics()
  return (
    <CellFrame sx={{ px: 5, pb: '60px' }}>
      <Typography variant="h3" sx={{ py: 2 }}>
        {t('collectionAll')}
      </Typography>
      <ScrollFetcher
        nodes={
          userCollections?.map(({ node: collection }, index) => (
            <CollectionBox
              key={index}
              collection={collection}
              fullWidth
              sx={{
                height: 'calc((100vw - 40px) * 190 / 335)',
                maxHeight: 'calc(560px * 190 / 335)',
              }}
              actionAreaProps={{
                onClick() {
                  analytics?.track('click_select', {
                    applicationKey: config.applicationKey,
                    artistId: collection.collection?.artistId,
                    collectionId: collection.id,
                    name: 'ArtistMyCollections',
                  })
                },
              }}
            />
          )) ?? []
        }
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 5 }}
        emptyMessage={t('collectionEmptyMessage')}
        loadingComponent={
          <Stack spacing={6} flexDirection="column" sx={{ py: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <CollectionBoxSkeleton fullWidth key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}
